import { ref, onMounted } from 'vue';
import axios from 'axios';
import { auth } from '@/firebase/init';
import { trendsStore } from '@/store/trends';

export function useTrendsAnalyzer() {
  // Initialize from store instead of hardcoded defaults
  const regionCode = ref(trendsStore.getters.currentRegion || 'US');
  const categoryId = ref(trendsStore.getters.currentCategory || '');
  const loading = ref(false);
  const error = ref(null);
  const results = ref(null);
  const recommendations = ref(null);
  const loadingRecommendations = ref(false);
  const recommendationsError = ref(null);
  const apiUrl = 'https://gettrends-zaeclqegra-uc.a.run.app';
  const categories = [
    { id: '', name: 'All Categories' },
    { id: '1', name: 'Film & Animation' },
    { id: '2', name: 'Autos & Vehicles' },
    { id: '10', name: 'Music' },
    { id: '15', name: 'Pets & Animals' },
    { id: '17', name: 'Sports' },
    { id: '19', name: 'Travel & Events' },
    { id: '20', name: 'Gaming' },
    { id: '22', name: 'People & Blogs' },
    { id: '23', name: 'Comedy' },
    { id: '24', name: 'Entertainment' },
    { id: '25', name: 'News & Politics' },
    { id: '26', name: 'Howto & Style' },
    { id: '27', name: 'Education' },
    { id: '28', name: 'Science & Technology' },
    { id: '29', name: 'Nonprofits & Activism' }
  ];
  const regions = [
    { code: 'US', name: 'United States' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'CA', name: 'Canada' },
    { code: 'AU', name: 'Australia' },
    { code: 'FR', name: 'France' },
    { code: 'DE', name: 'Germany' },
    { code: 'JP', name: 'Japan' },
    { code: 'IN', name: 'India' },
    { code: 'BR', name: 'Brazil' },
    { code: 'RU', name: 'Russia' },
    { code: 'KR', name: 'South Korea' },
    { code: 'MX', name: 'Mexico' },
    { code: 'ES', name: 'Spain' },
    { code: 'IT', name: 'Italy' },
    { code: 'ZA', name: 'South Africa' }
  ];

  // Sync with store on mount
  onMounted(() => {
    regionCode.value = trendsStore.getters.currentRegion || 'US';
    categoryId.value = trendsStore.getters.currentCategory || '';
  });

  async function analyzeTrends(forceRefresh = false) {
    loading.value = true;
    error.value = null;
    results.value = null;
    recommendations.value = null;
    
    try {
      // Check if user is authenticated
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Authentication required. Please log in to generate video ideas.');
      }

      // Get Firebase ID token for backend verification
      const idToken = await currentUser.getIdToken();
      
      const params = { 
        regionCode: regionCode.value,
        categoryId: categoryId.value || undefined,
        includeRecommendations: 'true',
        userId: currentUser.uid
      };
      
      if (forceRefresh) {
        params.forceRefresh = 'true';
        console.log('🔄 Force refreshing data from YouTube API...');
      }

      // Include Firebase ID token in Authorization header
      const response = await axios.get(apiUrl, { 
        params,
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Log cache status
      const cacheInfo = response.data.cacheInfo || {};
      if (cacheInfo.fromCache) {
        console.log(`💾 Using cached data (${cacheInfo.cachedMinutesAgo} minutes old)`);
        console.log(`📊 Cache will expire in ${(cacheInfo.cacheDurationHours * 60) - cacheInfo.cachedMinutesAgo} minutes`);
      } else {
        console.log('🆕 Fresh data fetched from YouTube API');
        console.log(`💾 Data cached for ${cacheInfo.cacheDurationHours} hours`);
      }
      
      // Log recommendations status
      if (response.data.recommendations) {
        const recCacheInfo = response.data.recommendations;
        if (recCacheInfo.fromCache) {
          console.log(`🤖 Using cached AI recommendations (${recCacheInfo.cachedHoursAgo} hours old)`);
        } else {
          console.log('🆕 Fresh AI recommendations generated');
        }
        console.log(`💡 Generated ${response.data.recommendations.recommendations?.length || 0} video ideas`);
      }
      
      // Log summary
      console.log(`📺 Found ${response.data.totalVideos} trending videos for ${regionCode.value}${categoryId.value ? ` in category ${categoryId.value}` : ''}`);
      
      results.value = {
        regionCode: response.data.regionCode,
        categoryId: response.data.categoryId,
        videos: response.data.videos,
        totalVideos: response.data.totalVideos,
        cacheInfo: cacheInfo
      };

      recommendations.value = response.data.recommendations;
    } catch (err) {
      console.error('❌ Error fetching trends:', err.response?.data?.error || err.message);
      
      // Handle different types of errors
      if (err.response?.status === 401) {
        error.value = 'Your session has expired. Please log in again.';
      } else if (err.response?.status === 403) {
        error.value = err.response.data?.error || 'You have exceeded your generation limit. Please upgrade your plan.';
      } else if (err.response?.status === 402) {
        error.value = err.response.data?.error || 'Payment required. Please upgrade your plan to continue.';
      } else if (err.message.includes('Authentication required')) {
        error.value = err.message;
      } else {
        error.value = err.response?.data?.error || 'Failed to analyze trends. Please try again.';
      }
    } finally {
      loading.value = false;
    }
  }

  async function getRecommendations(forceRefresh = false) {
    loadingRecommendations.value = true;
    recommendationsError.value = null;
    
    try {
      // Check if user is authenticated
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Authentication required. Please log in to get recommendations.');
      }

      // Get Firebase ID token for backend verification
      const idToken = await currentUser.getIdToken();
      
      const params = { 
        regionCode: regionCode.value,
        categoryId: categoryId.value || undefined,
        userId: currentUser.uid
      };
      
      if (forceRefresh) {
        params.forceRefresh = 'true';
      }
      
      // Use the correct recommendations API URL from deployment
      const recommendationsUrl = 'https://us-central1-trendztube.cloudfunctions.net/getRecommendations';
      const response = await axios.get(recommendationsUrl, { 
        params,
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      recommendations.value = response.data.recommendations;
      
      console.log(`🤖 Loaded ${response.data.recommendations?.recommendations?.length || 0} video recommendations`);
    } catch (err) {
      console.error('❌ Error fetching recommendations:', err.response?.data?.error || err.message);
      
      // Handle different types of errors
      if (err.response?.status === 401) {
        recommendationsError.value = 'Your session has expired. Please log in again.';
      } else if (err.response?.status === 403) {
        recommendationsError.value = err.response.data?.error || 'You have exceeded your generation limit. Please upgrade your plan.';
      } else if (err.message.includes('Authentication required')) {
        recommendationsError.value = err.message;
      } else {
        recommendationsError.value = err.response?.data?.error || 'Failed to get recommendations. Please try again.';
      }
    } finally {
      loadingRecommendations.value = false;
    }
  }

  function formatViews(count) {
    if (count >= 1000000) {
      return (count / 1000000).toFixed(1) + 'M';
    } else if (count >= 1000) {
      return (count / 1000).toFixed(1) + 'K';
    }
    return count.toString();
  }

  function formatDate(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 30) return `${diffDays} days ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  }

  return {
    regionCode,
    regions,
    categoryId,
    categories,
    loading,
    error,
    results,
    recommendations,
    loadingRecommendations,
    recommendationsError,
    analyzeTrends,
    getRecommendations,
    formatViews,
    formatDate
  };
}
